import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import apiClient from '@/lib/axios';
import { getAccessToken } from '@/lib/authSession';
import { API_BASE_URL } from '@/lib/apiOrigin';

const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
const SUPPORTED_BELL_KINDS = new Set([
    'overdue',
    'critical_failure',
    'task_started',
    'submission',
    'escalation',
]);

function safeJsonParse(s) {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((ch) => ch.charCodeAt(0)));
}

function logPushSubscribeFailure(err, phase) {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
  const ua = navigator.userAgent || '';
  const linux = /Linux/i.test(ua);
  const chromium = (/Chrome|Chromium/i.test(ua) && !/Edg/i.test(ua)) || /Brave/i.test(ua);
  const name = err?.name ?? 'Error';
  const msg = String(err?.message ?? err ?? '');
  console.warn('[Push] subscribe() failed', {
    phase, errorName: name, errorMessage: msg,
    isSecureContext: window.isSecureContext,
    likelyLinuxChromeFcm: linux && chromium && name === 'AbortError',
  });
}

function coalesceId(v) {
  if (v == null || v === '') return undefined;
  return String(v);
}

/** Matches dashboard `data-critical-alert-id` (e.g. task:uuid, project:uuid). */
function alertFocusIdFromPayload(data) {
  const ek = data?.entityKey;
  if (ek == null || String(ek).trim() === '') return undefined;
  return String(ek);
}

const META_BY_KIND = {
  overdue: 'Overdue',
  critical_failure: 'Critical Alert',
  task_started: 'Ready to Start',
  submission: 'Progress Update',
  escalation: 'Escalation',
};

/** Map a persisted DB row (from GET /notifications/unread) to a bell item. */
function dbRowToBellItem(row) {
  const data = row.data || {};
  const kind = row.kind || data.kind || 'overdue';
  return {
    id: row.id,
    dbId: row.id,
    kind,
    taskId: data.taskId ? String(data.taskId) : undefined,
    title: data.taskName || row.title || 'Notification',
    subtitle: row.body || data.body || '',
    meta: META_BY_KIND[kind] || kind,
    receivedAt: new Date(row.createdAt).getTime(),
    notificationId: data.notificationId,
    taskForceId: coalesceId(data.taskForceId),
    pillarId: coalesceId(data.pillarId),
    projectId: coalesceId(data.projectId),
    startDate: data.startDate,
    endDate: data.endDate,
    status: data.status,
    pillarName: data.pillarName,
    taskForceName: data.taskForceName,
    alertFocusId: alertFocusIdFromPayload(data),
  };
}

/** Map an SSE event payload to a bell item. */
function sseToBellItem(data, recipientUserId) {
  const kind = data?.kind || '';
  if (!SUPPORTED_BELL_KINDS.has(kind)) return null;
  const dbId = data?.dbNotificationId || null;
  return {
    id: dbId || `sse:${recipientUserId}:${kind}:${data?.notificationId || Date.now()}`,
    dbId,
    kind,
    taskId: data?.taskId ? String(data.taskId) : undefined,
    title: data?.taskName || data?.title || 'Notification',
    subtitle: data?.body || '',
    meta: META_BY_KIND[kind] || kind,
    receivedAt: Date.now(),
    notificationId: data?.notificationId,
    taskForceId: coalesceId(data?.taskForceId),
    pillarId: coalesceId(data?.pillarId),
    projectId: coalesceId(data?.projectId),
    startDate: data?.startDate,
    endDate: data?.endDate,
    status: data?.status,
    pillarName: data?.pillarName,
    taskForceName: data?.taskForceName,
    alertFocusId: alertFocusIdFromPayload(data),
  };
}

export function usePushNotifications() {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const userId = user?.userId != null ? String(user.userId) : null;
  const [items, setItems] = useState([]);
  const idSetRef = useRef(new Set());

  const markRead = useCallback(
    (bellId) => {
      setItems((prev) => {
        const item = prev.find((n) => n.id === bellId);
        if (item?.dbId) {
          apiClient.patch(`/notifications/${item.dbId}/read`).catch(() => {});
        }
        return prev.filter((n) => n.id !== bellId);
      });
    },
    [],
  );

  const markAllRead = useCallback(() => {
    apiClient.patch('/notifications/read-all').catch(() => {});
    setItems([]);
    idSetRef.current = new Set();
  }, []);

  // Fetch persisted unread on login / mount
  useEffect(() => {
    if (!userId || !isAuthenticated || isLoading) return;
    let cancelled = false;
    apiClient
      .get('/notifications/unread')
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const bells = rows.map(dbRowToBellItem).filter(Boolean);
        idSetRef.current = new Set(bells.map((n) => n.id));
        setItems(bells);
      })
      .catch((err) => {
        console.warn('[Notifications] failed to fetch unread:', err?.message ?? err);
      });
    return () => { cancelled = true; };
  }, [userId, isAuthenticated, isLoading]);

  const itemsForHeader = useMemo(
    () =>
      items.map((n) => ({
        ...n,
        onRead: () => markRead(n.id),
      })),
    [items, markRead],
  );

  // SSE live stream + optional Web Push
  useEffect(() => {
    if (!userId || !isAuthenticated || isLoading) return;
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

    let isCancelled = false;
    let eventSource = null;

    const addFromSse = (data) => {
      if (!userId) return;
      const bellItem = sseToBellItem(data, userId);
      if (!bellItem) return;
      if (idSetRef.current.has(bellItem.id)) return;
      idSetRef.current.add(bellItem.id);
      setItems((prev) => [bellItem, ...prev].slice(0, 50));
    };

    const setup = async () => {
      const accessToken = getAccessToken();

      if (accessToken) {
        const baseUrl =
          API_BASE_URL;
        const sseUrl = `${baseUrl}/notifications/sse?token=${encodeURIComponent(accessToken)}`;
        eventSource = new EventSource(sseUrl);

        eventSource.addEventListener('notification', (event) => {
          const data = safeJsonParse(event.data) || {};
          addFromSse(data);
        });

        eventSource.onerror = () => {};
      }

      if (isCancelled) return;

      // Web Push (optional, best-effort)
      try {
        const browserSecureContext =
          typeof window.isSecureContext === 'boolean'
            ? window.isSecureContext
            : location.protocol === 'https:' ||
              location.hostname === 'localhost' ||
              location.hostname === '127.0.0.1';
        const swPresent = 'serviceWorker' in navigator && navigator.serviceWorker != null;

        if (!vapidPublicKey || typeof Notification === 'undefined' || !browserSecureContext || !swPresent) return;
        if (Notification.permission === 'denied') return;

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        await navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
        const swRegistration = await navigator.serviceWorker.ready;

        let subscription = await swRegistration.pushManager.getSubscription();
        if (!subscription) {
          const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
          try {
            subscription = await swRegistration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
          } catch (firstErr) {
            logPushSubscribeFailure(firstErr, 'subscribe-first');
            const stale = await swRegistration.pushManager.getSubscription();
            if (stale) try { await stale.unsubscribe(); } catch { /* */ }
            try {
              subscription = await swRegistration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
            } catch (retryErr) {
              logPushSubscribeFailure(retryErr, 'subscribe-retry');
              throw retryErr;
            }
          }
        }

        const subscriptionPayload = subscription.toJSON ? subscription.toJSON() : subscription;
        try {
          await apiClient.post('/notifications/save-subscription', { subscription: subscriptionPayload });
        } catch { /* */ }

        if (isCancelled) return;

        const swMessageHandler = (event) => {
          if (event?.data?.type === 'push-notification-click') {
            const clickData = event.data?.data || {};
            const notificationId = event.data?.notificationId;
            const kind = clickData?.kind || 'overdue';
            if (!notificationId || !userId) return;
            markRead(`push:${userId}:${kind}:${notificationId}`);
          }
        };
        navigator.serviceWorker.addEventListener('message', swMessageHandler);
        return () => {
          navigator.serviceWorker.removeEventListener('message', swMessageHandler);
        };
      } catch (pushErr) {
        console.warn('[Push] Web Push registration aborted (SSE still active).', pushErr?.name, pushErr?.message);
      }
    };

    let cleanup = null;
    setup().then((fn) => { cleanup = fn; }).catch(() => {});

    return () => {
      isCancelled = true;
      if (eventSource) eventSource.close();
      if (typeof cleanup === 'function') cleanup();
    };
  }, [userId, isAuthenticated, isLoading, markRead]);

  return { items: itemsForHeader, markAllRead };
}
